/**
 * Duplicate Instrument Dialog Component
 *
 * Dialog shown when linking a position to an instrument that is already
 * linked to another position within the same user's portfolio.
 *
 * Presents options:
 * - Add: Merge quantity with weighted average cost pricing
 * - Replace: Delete existing and link new position
 * - Cancel: Do nothing
 *
 * NOW THEME-AWARE and i18n SUPPORTED
 */

import React, { useMemo } from 'react';
import { View, Text, StyleSheet, Modal, TouchableOpacity } from 'react-native';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../contexts/ThemeContext';

export interface PositionSnapshot {
  id: number;
  symbol: string;
  quantity: number;
  cost_price: number;
  currency?: string;
}

interface DuplicateInstrumentDialogProps {
  visible: boolean;
  existingPosition: PositionSnapshot;
  newPosition: {
    symbol: string;
    quantity: number;
    cost_price?: number;
  };
  onAdd: () => void;
  onReplace: () => void;
  onCancel: () => void;
  loading?: boolean;
}

export function DuplicateInstrumentDialog({
  visible,
  existingPosition,
  newPosition,
  onAdd,
  onReplace,
  onCancel,
  loading = false,
}: DuplicateInstrumentDialogProps) {
  const { theme } = useTheme();
  const { t } = useTranslation();
  const styles = useMemo(() => createThemedStyles(theme), [theme]);

  // Convert API string values to numbers for calculations
  const existingQty = Number(existingPosition.quantity) || 0;
  const existingCost = Number(existingPosition.cost_price) || 0;
  const newQty = Number(newPosition.quantity) || 0;
  const newCost = Number(newPosition.cost_price) || 0;

  // Calculate weighted average cost for preview
  const calculateWeightedCost = () => {
    const totalQty = existingQty + newQty;
    if (totalQty > 0) {
      return (existingQty * existingCost + newQty * newCost) / totalQty;
    }
    return existingCost;
  };

  const weightedCost = calculateWeightedCost();
  const totalQuantity = existingQty + newQty;

  // Helper to safely format numbers
  const formatNumber = (
    value: number | string | undefined | null,
    decimals: number = 2
  ): string => {
    const num = Number(value);
    if (isNaN(num)) return 'N/A';
    return num.toFixed(decimals);
  };

  return (
    <Modal visible={visible} transparent animationType="fade" onRequestClose={onCancel}>
      <View style={styles.overlay}>
        <View style={styles.dialog}>
          {/* Warning icon */}
          <View style={styles.iconContainer}>
            <Text style={styles.icon}>⚠️</Text>
          </View>

          {/* Title */}
          <Text style={[styles.title, { color: theme.colors.warning }]}>
            {t('portfolio.duplicateInstrumentTitle', { defaultValue: 'Instrument Already Linked' })}
          </Text>

          {/* Message */}
          <Text style={[styles.message, { color: theme.colors.text }]}>
            {t('portfolio.duplicateInstrumentMessage', {
              defaultValue:
                'This instrument is already linked to position {{symbol}} ({{quantity}} shares @ {{price}}). How would you like to proceed?',
              symbol: existingPosition.symbol,
              quantity: existingQty,
              price: formatNumber(existingPosition.cost_price),
            })}
          </Text>

          {/* Position details comparison */}
          <View
            style={[
              styles.detailsContainer,
              {
                backgroundColor: theme.colors.backgroundElevated,
                borderColor: theme.colors.border,
              },
            ]}
          >
            {/* Existing position */}
            <View style={styles.positionRow}>
              <View style={styles.positionLabelContainer}>
                <Text style={[styles.positionLabel, { color: theme.colors.textSecondary }]}>
                  {t('portfolio.existingPosition', { defaultValue: 'Existing Position' })}:
                </Text>
              </View>
              <View style={styles.positionDetails}>
                <Text style={[styles.positionSymbol, { color: theme.colors.text }]}>
                  {existingPosition.symbol}
                </Text>
                <Text style={[styles.positionInfo, { color: theme.colors.textSecondary }]}>
                  {existingQty} {t('portfolio.shares', { defaultValue: 'shares' })} @{' '}
                  {formatNumber(existingPosition.cost_price)}
                </Text>
              </View>
            </View>

            {/* Divider */}
            <View style={[styles.divider, { backgroundColor: theme.colors.border }]} />

            {/* New position */}
            <View style={styles.positionRow}>
              <View style={styles.positionLabelContainer}>
                <Text style={[styles.positionLabel, { color: theme.colors.textSecondary }]}>
                  {t('portfolio.newPosition', { defaultValue: 'New Position' })}:
                </Text>
              </View>
              <View style={styles.positionDetails}>
                <Text style={[styles.positionSymbol, { color: theme.colors.text }]}>
                  {newPosition.symbol}
                </Text>
                <Text style={[styles.positionInfo, { color: theme.colors.textSecondary }]}>
                  {newQty} {t('portfolio.shares', { defaultValue: 'shares' })} @{' '}
                  {formatNumber(newPosition.cost_price)}
                </Text>
              </View>
            </View>

            {/* Merged result preview for Add action */}
            <View
              style={[
                styles.mergedResult,
                { backgroundColor: theme.colors.background, borderColor: theme.colors.primary },
              ]}
            >
              <Text style={[styles.mergedTitle, { color: theme.colors.primary }]}>
                {t('portfolio.mergedResult', { defaultValue: 'Merged Result (Add)' })}
              </Text>
              <Text style={[styles.mergedInfo, { color: theme.colors.text }]}>
                {totalQuantity} {t('portfolio.shares', { defaultValue: 'shares' })} @{' '}
                {formatNumber(weightedCost)}
              </Text>
            </View>
          </View>

          {/* Buttons */}
          <View style={styles.buttonContainer}>
            {/* Cancel button */}
            <TouchableOpacity
              style={[
                styles.button,
                styles.cancelButton,
                {
                  backgroundColor: theme.colors.backgroundElevated,
                  borderWidth: theme.id === 'bauhaus' ? 2 : 1,
                  borderColor: theme.colors.border,
                },
              ]}
              onPress={onCancel}
              disabled={loading}
            >
              <Text style={[styles.buttonText, { color: theme.colors.textSecondary }]}>
                {t('common.cancel', { defaultValue: 'Cancel' })}
              </Text>
            </TouchableOpacity>

            {/* Replace button */}
            <TouchableOpacity
              style={[styles.button, styles.replaceButton, { backgroundColor: theme.colors.error }]}
              onPress={onReplace}
              disabled={loading}
            >
              <Text style={[styles.buttonText, { color: theme.colors.textInverse }]}>
                {t('portfolio.replaceTheExisting', { defaultValue: 'Replace the existing' })}
              </Text>
              <Text style={[styles.buttonDescription, { color: theme.colors.textInverse }]}>
                {t('portfolio.replacePositionDescription', {
                  defaultValue: 'Delete existing position and link this one',
                })}
              </Text>
            </TouchableOpacity>

            {/* Add button */}
            <TouchableOpacity
              style={[styles.button, styles.addButton, { backgroundColor: theme.colors.success }]}
              onPress={onAdd}
              disabled={loading}
            >
              <Text style={[styles.buttonText, { color: theme.colors.textInverse }]}>
                {t('portfolio.addToExisting', { defaultValue: 'Add to existing' })}
              </Text>
              <Text style={[styles.buttonDescription, { color: theme.colors.textInverse }]}>
                {t('portfolio.addQuantityDescription', {
                  defaultValue: 'Merge quantities with weighted average cost',
                })}
              </Text>
            </TouchableOpacity>
          </View>
        </View>
      </View>
    </Modal>
  );
}

/**
 * Create themed styles dynamically
 */
function createThemedStyles(theme: any) {
  return StyleSheet.create({
    overlay: {
      flex: 1,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      justifyContent: 'center',
      alignItems: 'center',
      padding: 20,
    },
    dialog: {
      backgroundColor: theme.colors.surface,
      borderRadius: theme.radii.lg,
      padding: 24,
      width: '100%',
      maxWidth: 420,
      borderWidth: theme.id === 'bauhaus' ? 2 : 0,
      borderColor: theme.colors.border,
      shadowColor: theme.id === 'bauhaus' ? '#000' : theme.colors.primary,
      shadowOffset: theme.id === 'bauhaus' ? { width: 8, height: 8 } : { width: 0, height: 2 },
      shadowOpacity: theme.id === 'bauhaus' ? 1 : 0.25,
      shadowRadius: theme.id === 'bauhaus' ? 0 : 8,
      elevation: 5,
    },
    iconContainer: {
      alignItems: 'center',
      marginBottom: 16,
    },
    icon: {
      fontSize: 48,
    },
    title: {
      fontSize: theme.typography.fontSizeXl,
      fontWeight: theme.typography.fontWeightBold,
      marginBottom: 12,
      textAlign: 'center',
      textTransform: theme.id === 'bauhaus' ? 'uppercase' : 'none',
    },
    message: {
      fontSize: theme.typography.fontSizeMd,
      marginBottom: 16,
      textAlign: 'center',
      lineHeight: 22,
    },
    detailsContainer: {
      borderRadius: theme.radii.md,
      padding: 16,
      marginBottom: 20,
      borderWidth: theme.id === 'bauhaus' ? 2 : 1,
    },
    positionRow: {
      flexDirection: 'row',
      alignItems: 'center',
      paddingVertical: 8,
    },
    positionLabelContainer: {
      flex: 1,
    },
    positionLabel: {
      fontSize: theme.typography.fontSizeSm,
      fontWeight: theme.typography.fontWeightMedium,
    },
    positionDetails: {
      flex: 2,
      alignItems: 'flex-end',
    },
    positionSymbol: {
      fontSize: theme.typography.fontSizeMd,
      fontWeight: theme.typography.fontWeightSemibold,
    },
    positionInfo: {
      fontSize: theme.typography.fontSizeSm,
      marginTop: 2,
    },
    divider: {
      height: theme.id === 'bauhaus' ? 2 : 1,
      marginVertical: 8,
    },
    mergedResult: {
      marginTop: 8,
      padding: 12,
      borderRadius: theme.radii.sm,
      borderWidth: theme.id === 'bauhaus' ? 2 : 1,
      alignItems: 'center',
    },
    mergedTitle: {
      fontSize: theme.typography.fontSizeSm,
      fontWeight: theme.typography.fontWeightSemibold,
      marginBottom: 4,
      textTransform: theme.id === 'bauhaus' ? 'uppercase' : 'none',
    },
    mergedInfo: {
      fontSize: theme.typography.fontSizeMd,
      fontWeight: theme.typography.fontWeightBold,
    },
    buttonContainer: {
      flexDirection: 'column',
      gap: 10,
    },
    button: {
      paddingVertical: 14,
      paddingHorizontal: 24,
      borderRadius: theme.radii.md,
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: 52,
    },
    cancelButton: {
      flex: 0,
    },
    replaceButton: {
      flex: 1,
    },
    addButton: {
      flex: 1,
    },
    buttonText: {
      fontSize: theme.typography.fontSizeMd,
      fontWeight: theme.typography.fontWeightSemibold,
      textTransform: theme.id === 'bauhaus' ? 'uppercase' : 'none',
    },
    buttonDescription: {
      fontSize: theme.typography.fontSizeXs,
      marginTop: 2,
      opacity: 0.9,
    },
  });
}
